import { marketSize } from '@pogi/market-units';
/*
 * pogi-sizing-modal — PDP buy-box "sizing aid" modals (vanilla, no deps).
 * ---------------------------------------------------------------------------
 * Two tools, one shell:
 *   kind="pads"        → Pad Finder      (one Q: dog size → recommends Size)
 *   kind="bags"        → Bag Calculator  (daily use + supply target → Count)
 *   kind="compostable" → Bag Calculator  (same; no scent axis)
 * Wipes gets NONE (deliberate product decision).
 *
 * Markup is rendered by snippets/pogi-sizing-modal.liquid INSIDE each buy-box
 * block, so the modal can reach its sibling <pogi-buy-box-*> element.
 *
 * Pads write back the recommended Size through the buy box's own option button.
 * A deliberate bag-style choice delegates to the buy box's own Format button,
 * then Bags hand the recommended Count to the buy box's public sizing bridge.
 * Quantity, scent, purchase mode and cadence stay buy-box-owned. The calculator
 * applies a pack choice for review; only the main buy-box action adds to cart.
 * The modal never silently toggles Subscribe or invents a plan.
 *
 * ⚠ HEURISTICS — none of these are sourced data. A vet/ops gut-check is the
 * only thing between them and being wrong in public. Confirm before/after launch.
 */
(() => {
  'use strict';

  /* i18n: JA overrides come from the [data-sm-i18n] blob the snippet renders via
     `| t`. EVERY read falls back to the exact English literal that used to be
     hardcoded, so a missing key can never change the EN site. [[name]] placeholder
     (not {{ }}) so Liquid's t-filter never eats it. The blob is parsed once per
     modal in the constructor; the reader is the per-instance this.t(). */
  const smTpl = (I, k, fb, vars) => {
    let s = (I && I[k] != null) ? I[k] : fb;
    if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
    return s;
  };
  const setTranslatedText = (element, value) => {
    if (!element) return;
    if (typeof window.POGI_SET_TRANSLATED_TEXT === 'function') window.POGI_SET_TRANSLATED_TEXT(element, value);
    else element.textContent = value == null ? '' : String(value);
  };

  /* ---- ⚠ HEURISTIC CONSTANTS — researched June 2026, still warrant a
     vet/ops gut-check before relying on them in public. Source per value. -- */

  // Pads "~N a month" context line: assumed pad changes/day (month = changes*30).
  // Research: puppies in active training change 3–4×/day (vets advise "at least
  // 3× a day"); maintenance/senior use ~1–2×/day. 2 = conservative-typical central
  // estimate across that mix. ⚠ training-heavy buyers run higher.
  //   Porch Potty: https://porchpotty.com/blogs/news/how-often-to-change-puppy-training-pads
  //   Bulldogology: https://www.bulldogology.net/how-often-should-i-change-my-dogs-pee-pad/
  const PAD_CHANGES_PER_DAY = 2;

  // Dog-size band → recommended live Size option value (exact label match).
  // Bands CONFIRMED by independent pee-pad sizing guides: Medium 18×24 for small
  // dogs & puppies (<25 lb), Large 24×24 for most adults (25–60 lb), XL 24×35 for
  // big/senior dogs (60+ lb). Universal advice: "when in doubt, size up."
  //   Scott Brand: https://www.scottbrand.com/en-us/tips-and-advice/pet-care/finding-right-dog-pad
  //   Bark Potty: https://barkpotty.com/blogs/bark-blog/bark-potty-pad-size-guide-matching-pad-dimensions-to-your-dog-s-breed
  //   Four Paws: https://www.fourpaws.com/pets-101/potty-time-and-training/selecting-wee-wee-pad-for-your-dog
  // rank = the size's position in the buy box's size axis (Medium=0, Large=1, XL=2).
  // Write-back selects the rank-th [data-pogi-tile] size tile, NOT a localized string,
  // so "Use this size" works on EN and /ja alike (Shopify translates the option-value
  // text but preserves option-value ORDER across locales). sizeValue kept only as an
  // EN-store fallback / for the result name.
  // Dims are shown in the LOCALE'S unit: EN keeps INCHES (w/h, US convention) byte-
  // /ja shows CENTIMETRES rounded to NICE numbers (owner rule, not raw ceil):
  // M 45×60, L 60×60, XL 60×90. The `dims` t() gets all four; each locale's pogi.sm.dims picks its pair.
  const PAD_MAP = {
    small:  { rank: 0, sizeValue: 'Medium (18 x 24 in)',      name: 'Medium',      w: 18, h: 24, wc: 45, hc: 60 },
    medium: { rank: 1, sizeValue: 'Large (24 x 24 in)',       name: 'Large',       w: 24, h: 24, wc: 60, hc: 60 },
    large:  { rank: 2, sizeValue: 'Extra Large (24 x 35 in)', name: 'Extra Large', w: 24, h: 35, wc: 60, hc: 90 },
  };
  // Glyph footprint uses cm so its proportions match the /ja dims; EN ratios identical
  // (cm is a linear scaling of in), so the footprint looks the same on both stores.
  const PAD_MAX_DIM = 90; // shared yardstick (cm) so XL reads biggest in the glyph

  // Shopper-editable daily use is shared with the existing buy-box estimate.
  // The fallback only applies when a buy box has no daily-usage API.
  const BAG_DEFAULT_PER_DAY = 2;
  // Four months is an editable starting target, not a consumption prediction.
  // The calculator labels its 30-day-month assumption explicitly.
  const BAG_STARTING_TARGET_MONTHS = 4;
  const BAG_STARTING_TARGET_DAYS = 120;
  // A low-pressure assist for the complex Bags buy box. The existing localized
  // calculator trigger gets one visual lift only after it has stayed in view
  // untouched for 12 seconds. It never opens the modal or announces itself.
  const BAG_NUDGE_DELAY_MS = 12000;

  /* ---- inline icons (self-hosted; no unpkg dependency) ----------------- */
  // Lucide "dog" icon (self-hosted, no unpkg dependency).
  const SVG_DOG = (px) => `<svg width="${px}" height="${px}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11.25 16.25h1.5L12 17z"/><path d="M16 14v.5"/><path d="M4.42 11.247A13.152 13.152 0 0 0 4 14.556C4 18.728 7.582 21 12 21s8-2.272 8-6.444a11.702 11.702 0 0 0-.493-3.309"/><path d="M8 14v.5"/><path d="M8.5 8.5c-.384 1.05-1.083 2.028-2.344 2.5-1.931.722-3.576-.297-3.656-1-.113-.994 1.177-6.53 4-7 1.923-.321 3.651.845 3.651 2.235A7.497 7.497 0 0 1 14 5.277c0-1.39 1.844-2.598 3.767-2.277 2.823.47 4.113 6.006 4 7-.08.703-1.725 1.722-3.656 1-1.261-.472-1.855-1.45-2.239-2.5"/></svg>`;
  const SVG_RULER = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21.3 8.7-8.6 8.6a1 1 0 0 1-1.4 0l-2.6-2.6a1 1 0 0 1 0-1.4l8.6-8.6a1 1 0 0 1 1.4 0l2.6 2.6a1 1 0 0 1 0 1.4Z"/><path d="m7.5 10.5 2 2"/><path d="m10.5 7.5 2 2"/><path d="m13.5 4.5 2 2"/><path d="m4.5 13.5 2 2"/></svg>`;

  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const bagsIn = (label) => {
    const text = String(label || '');
    // Word order is not locale-stable: Portuguese may put bags first
    // ("270 sacos · 18 rolos") while Dutch puts rolls first. In the current
    // catalog the bag count is always the largest exact numeric token.
    const numbers = text.match(/[0-9][0-9.,\s\u00a0\u202f]*/g) || [];
    const counts = numbers
      .map((raw) => parseInt(raw.replace(/[^0-9]/g, ''), 10))
      .filter((count) => Number.isFinite(count) && count > 0);
    return counts.length ? Math.max(...counts) : 0;
  };
  const packParts = (label) => {
    const text = String(label || '').trim();
    const paren = text.match(/^(.+?)\s*[（(]([^）)]+)[）)]\s*$/);
    if (paren) return { headline: paren[1].trim(), detail: paren[2].trim() };
    const dot = text.indexOf('·');
    if (dot >= 0) return { headline: text.slice(0, dot).trim(), detail: text.slice(dot + 1).trim() };
    return { headline: text, detail: '' };
  };
  const boundedInteger = (value, maximum, fallback) => {
    const number = Number(value);
    return String(value ?? '').trim() && Number.isFinite(number)
      ? Math.min(maximum, Math.max(1, Math.round(number)))
      : fallback;
  };
  const recommendBagCount = (counts, perDay, targetDays = BAG_STARTING_TARGET_DAYS, quantity = 1) => {
    const safePerDay = Number(perDay);
    const safeTargetDays = Number(targetDays);
    const safeQuantity = Number(quantity);
    if (!Array.isArray(counts) || !counts.length || !Number.isFinite(safePerDay) || !(safePerDay > 0)
      || !Number.isFinite(safeTargetDays) || !(safeTargetDays > 0)
      || !Number.isSafeInteger(safeQuantity) || safeQuantity < 1) return null;
    const sorted = counts
      .filter((count) => count && Number.isSafeInteger(Number(count.bags)) && Number(count.bags) > 0
        && Number.isSafeInteger(Number(count.bags) * safeQuantity))
      .slice()
      .sort((a, b) => Number(a.bags) - Number(b.bags));
    if (!sorted.length) return null;
    const targetBags = safePerDay * safeTargetDays;
    const pick = sorted.find((count) => Number(count.bags) * safeQuantity >= targetBags) || sorted[sorted.length - 1];
    const totalBags = Number(pick.bags) * safeQuantity;
    const coverageDays = totalBags / safePerDay;
    return {
      pick,
      targetBags,
      targetDays: safeTargetDays,
      quantity: safeQuantity,
      totalBags,
      coverageDays,
      coverageMonths: coverageDays / 30,
      shortfall: coverageDays < safeTargetDays,
    };
  };

  // Pure recommendation seam for deterministic regression tests. The DOM
  // controller consumes these same functions; this is not a second formula.
  window.POGI_SIZING_CALCULATOR = Object.freeze({
    BAG_DEFAULT_PER_DAY,
    BAG_STARTING_TARGET_MONTHS,
    BAG_STARTING_TARGET_DAYS,
    boundedInteger,
    recommendBagCount,
    packParts,
  });

  /* ---- the controller, one per modal ----------------------------------- */
  let modalSequence = 0;
  class SizingModal {
    constructor(dialog) {
      this.dialog = dialog;
      this.kind = dialog.dataset.kind; // pads | bags | compostable
      this.formatGroupId = `pogi-sm-formats-${this.kind}-${++modalSequence}`;
      // i18n blob (JA overrides) rendered by the snippet; EN fallbacks stay inline.
      const i18nEl = dialog.querySelector('[data-sm-i18n]');
      this.I = {};
      if (i18nEl) { try { this.I = JSON.parse(i18nEl.textContent); } catch (e) { /* keep EN fallbacks */ } }
      this.t = (k, fb, vars) => smTpl(this.I, k, fb, vars);
      try { this.numberFormat = new Intl.NumberFormat(document.documentElement.lang || undefined); }
      catch (_) { this.numberFormat = new Intl.NumberFormat(); }
      // bags use [data-pogi-size] count pills; compostable uses [data-pogi-count] cards.
      this.countSel = this.kind === 'compostable' ? '[data-pogi-count]' : '[data-pogi-size]';
      this.buyBox = dialog.closest('pogi-buy-box-pads, pogi-buy-box-bags, pogi-buy-box-compostable')
        || (dialog.closest('.shopify-section') || document).querySelector('pogi-buy-box-pads, pogi-buy-box-bags, pogi-buy-box-compostable');
      this.body = dialog.querySelector('[data-sm-body]');
      this.cta = dialog.querySelector('[data-sm-cta]');
      this.help = dialog.querySelector('[data-sm-help]');
      this.rec = null;
      this.busy = false;

      // open / close
      const root = this.buyBox || (dialog.closest('.shopify-section') || document);
      this.triggers = [...root.querySelectorAll('[data-pogi-sizing-open]')];
      this.triggers.forEach((b) => {
        b.addEventListener('click', () => {
          this.dismissNudge();
          this.open();
        });
      });
      dialog.querySelector('[data-sm-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); }); // backdrop
      // First Escape dismisses the progressively disclosed style chooser. A
      // second Escape keeps the native <dialog> close behavior.
      dialog.addEventListener('cancel', (event) => {
        if (!this.formatChooserOpen()) return;
        event.preventDefault();
        this.closeFormatChooser({ focus: true });
      });
      this.cta?.addEventListener('click', () => {
        // Never imply success by closing when no locale-safe recommendation can
        // be resolved or written back to a live option control.
        if (this.busy || !this.inputsValid() || !this.apply()) return;
        dialog.close();
        if (this.kind !== 'pads') {
          const value = this.reviewValue;
          requestAnimationFrame(() => {
            const chosen = [...(this.buyBox?.querySelectorAll(this.countSel) || [])]
              .find((button) => (button.dataset.pogiSize || button.dataset.pogiCount) === value);
            if (chosen?.isConnected) chosen.focus();
          });
        }
      });

      this.state = this.kind === 'pads'
        ? { size: 'medium' }
        : { perDay: this.currentDailyUsage(), targetMonths: BAG_STARTING_TARGET_MONTHS };
      this.render();           // build the question UI once
      this.compute();          // live result from defaults
      if (this.kind !== 'pads') this.setupNudge();
    }

    setupNudge() {
      const trigger = this.triggers?.[0];
      if (!trigger || !this.buyBox) return;
      this.nudgeDismissed = false;
      this.nudgeTimer = 0;
      const configuredDelay = Number(this.dialog.dataset.smNudgeDelayMs);
      const delay = Number.isFinite(configuredDelay) && configuredDelay >= 0
        ? configuredDelay
        : BAG_NUDGE_DELAY_MS;
      const clearTimer = () => {
        if (!this.nudgeTimer) return;
        window.clearTimeout(this.nudgeTimer);
        this.nudgeTimer = 0;
      };
      const arm = () => {
        if (this.nudgeDismissed || this.nudgeTimer || trigger.classList.contains('is-nudged')) return;
        this.nudgeTimer = window.setTimeout(() => {
          this.nudgeTimer = 0;
          if (!this.nudgeDismissed && !this.dialog.open) trigger.classList.add('is-nudged');
        }, delay);
      };
      this.clearNudgeTimer = clearTimer;

      // Once a shopper uses any real commerce control, treat that as confident
      // progress and leave the calculator link quiet for the rest of the view.
      const commerceControl = [
        '[data-pogi-format]', '[data-pogi-size]', '[data-pogi-count]', '[data-pogi-tile]',
        '[data-pogi-mode]', '[data-pogi-freq]', '[data-pogi-qty-dec]', '[data-pogi-qty-inc]',
        '[data-pogi-atc]',
      ].join(',');
      this.buyBox.addEventListener('click', (event) => {
        if (event.target.closest(commerceControl)) this.dismissNudge();
      });

      if (typeof window.IntersectionObserver === 'function') {
        this.nudgeObserver = new window.IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6)) arm();
          else clearTimer();
        }, { threshold: [0, 0.6] });
        this.nudgeObserver.observe(trigger);
      } else {
        arm();
      }
    }

    dismissNudge() {
      this.nudgeDismissed = true;
      this.clearNudgeTimer?.();
      this.nudgeObserver?.disconnect();
      this.triggers?.forEach((trigger) => trigger.classList.remove('is-nudged'));
    }

    padName(p) {
      if (!this.buyBox) return p.name;
      const tiles = [...this.buyBox.querySelectorAll('[data-pogi-tile]')];
      const tile = p.rank != null && p.rank < tiles.length ? tiles[p.rank] : null;
      const visibleName = tile?.querySelector('.pogi-bb__sizetile-name')?.textContent?.trim();
      if (visibleName) return visibleName;
      const raw = tile?.dataset?.pogiTile || '';
      return String(raw).split(/[（(]/)[0].trim() || p.name;
    }

    open() {
      if (this.kind !== 'pads') {
        this.state.perDay = this.currentDailyUsage();
        this.syncNumericInputs();
        this.renderFormatOptions();
        this.closeFormatChooser({ focus: false });
      }
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal(); else this.dialog.setAttribute('open', '');
      // Recompute against the buy box's CURRENT format/state on every open, so the
      // result + helper line always match what's selected (and what write-back applies)
      // even if the shopper changed format before opening.
      this.compute();
    }

    currentDailyUsage() {
      const value = this.buyBox?.getDailyUsage?.();
      const fallback = this.kind === 'pads' ? PAD_CHANGES_PER_DAY : BAG_DEFAULT_PER_DAY;
      return boundedInteger(value ?? this.state?.perDay, 100, fallback);
    }

    currentQuantity() {
      const value = Number(this.buyBox?.qty);
      return Number.isSafeInteger(value) && value > 0 ? value : 1;
    }

    inputsValid() {
      return this.kind === 'pads' || [...this.body.querySelectorAll('[data-sm-daily], [data-sm-target-months]')]
        .every((input) => input.validity.valid);
    }

    syncNumericInputs() {
      const daily = this.body?.querySelector('[data-sm-daily]');
      const target = this.body?.querySelector('[data-sm-target-months]');
      if (daily) daily.value = this.state.perDay;
      if (target) target.value = this.state.targetMonths;
      for (const input of [daily, target]) input?.removeAttribute('aria-invalid');
    }

    changeAssumption(input, commit) {
      const daily = input.hasAttribute('data-sm-daily');
      const key = daily ? 'perDay' : 'targetMonths';
      if (!commit && !input.validity.valid) {
        input.setAttribute('aria-invalid', 'true');
        this.compute();
        return;
      }
      this.state[key] = boundedInteger(input.value, daily ? 100 : 12, this.state[key]);
      if (daily && typeof this.buyBox?.setDailyUsage === 'function') {
        this.buyBox.setDailyUsage(this.state.perDay);
        this.state.perDay = this.currentDailyUsage();
      }
      input.value = this.state[key];
      input.removeAttribute('aria-invalid');
      this.compute();
    }

    /* ---- build the inputs (once) --------------------------------------- */
    render() {
      if (this.kind === 'pads') {
        const us = this.dialog.dataset.country === 'US';
        const low = this.numberFormat.format(us ? 25 : 11.3);
        const high = this.numberFormat.format(us ? 60 : 27.2);
        const unit = us ? 'lb' : 'kg';
        const qDogSize = this.t('q_dog_size', 'Dog size');
        this.body.innerHTML = `
          <div class="pogi-sm__qlabel">${esc(qDogSize)}</div>
          <div class="pogi-sm__seg pogi-sm__seg--3" role="radiogroup" aria-label="${esc(qDogSize)}" data-sm-seg="size">
            ${[['small', this.t('dog_small', 'Small'), '&lt; ' + low + ' ' + unit, 17], ['medium', this.t('dog_medium', 'Medium'), low + '–' + high + ' ' + unit, 22], ['large', this.t('dog_large', 'Large'), high + '+ ' + unit, 28]]
              .map(([v, l, sub, ic]) => `
              <button type="button" role="radio" class="pogi-sm__tile" data-val="${v}" aria-checked="false">
                <span class="pogi-sm__tile-ic">${SVG_DOG(ic)}</span>
                <span class="pogi-sm__tile-l">${l}</span>
                <span class="pogi-sm__tile-sub">${sub}</span>
              </button>`).join('')}
          </div>
          <div class="pogi-sm__result" data-sm-result></div>`;
        this.body.querySelector('[data-sm-seg="size"]').addEventListener('click', (e) => {
          const b = e.target.closest('[data-val]'); if (!b) return;
          this.state.size = b.dataset.val; this.compute();
        });
      } else {
        this.body.innerHTML = `
          <label class="pogi-sm__row">
            <span class="pogi-sm__qlabel">${esc(this.t('ue_daily_bags', 'Bags per day'))}</span>
            <input class="pogi-sm__number" type="number" inputmode="numeric" min="1" max="100" step="1" required data-sm-daily value="${this.state.perDay}">
          </label>
          <label class="pogi-sm__row">
            <span class="pogi-sm__qlabel">${esc(this.t('ue_calc_target', 'Months of supply'))}</span>
            <input class="pogi-sm__number" type="number" inputmode="numeric" min="1" max="12" step="1" required data-sm-target-months value="${this.state.targetMonths}">
          </label>
          <div class="pogi-sm__format-section" data-sm-format-section hidden>
            <div class="pogi-sm__format-summary">
              <span class="pogi-sm__format-current-glyph" data-sm-format-current-glyph aria-hidden="true"></span>
              <span class="pogi-sm__format-summary-label">${esc(this.t('bags_q_format', 'Bag style'))}</span>
              <strong class="pogi-sm__format-current" data-sm-format-current></strong>
              <button type="button" class="pogi-sm__format-toggle" data-sm-format-toggle
                aria-expanded="false" aria-controls="${esc(this.formatGroupId)}">
                ${esc(this.t('bags_format_change', 'Change'))}
              </button>
            </div>
            <span class="visually-hidden" data-sm-format-announcer role="status" aria-live="polite"></span>
            <div class="pogi-sm__format-chooser" data-sm-format-chooser hidden>
              <div id="${esc(this.formatGroupId)}" class="pogi-sm__formats" role="radiogroup"
                aria-label="${esc(this.t('bags_q_format', 'Bag style'))}" data-sm-formats></div>
            </div>
          </div>
          <div class="pogi-sm__result" data-sm-result role="status" aria-live="polite"></div>`;
        this.body.querySelectorAll('[data-sm-daily], [data-sm-target-months]').forEach((input) => {
          input.addEventListener('input', () => this.changeAssumption(input, false));
          input.addEventListener('change', () => this.changeAssumption(input, true));
        });
        const formats = this.body.querySelector('[data-sm-formats]');
        formats?.addEventListener('click', (event) => {
          const option = event.target.closest('[data-sm-format]');
          if (option) this.selectFormat(option);
        });
        formats?.addEventListener('keydown', (event) => this.roveFormat(event));
        this.body.querySelector('[data-sm-format-toggle]')?.addEventListener('click', () => {
          if (this.formatChooserOpen()) this.closeFormatChooser({ focus: true });
          else this.openFormatChooser();
        });
        this.renderFormatOptions();
      }
    }

    /* ---- live compute + result ----------------------------------------- */
    compute() {
      // reflect selected inputs
      this.body.querySelectorAll('[data-sm-seg] [data-val]').forEach((b) => {
        const seg = b.closest('[data-sm-seg]').dataset.smSeg;
        b.setAttribute('aria-checked', String(b.dataset.val === this.state[seg]));
      });
      const out = this.body.querySelector('[data-sm-result]');
      if (this.kind === 'pads') {
        out.classList.remove('pogi-sm__result--bags');
        const p = PAD_MAP[this.state.size];
        const padName = this.padName(p);
        const changes = this.currentDailyUsage();
        const month = changes * 30;
        this.rec = { rank: p.rank, sizeValue: p.sizeValue };
        const scale = 84 / PAD_MAX_DIM; // PAD_MAX_DIM is cm → use the cm footprint
        // dims t() carries BOTH unit pairs; the locale's pogi.sm.dims picks one
        // (EN → "[[w]] × [[h]] in", JA → "[[wc]] × [[hc]] cm"). EN stays byte-identical.
        out.innerHTML = `
          <span class="pogi-sm__eyebrow">${esc(this.t('best_fit', 'Best fit'))}</span>
          <div class="pogi-sm__big">${esc(padName)}</div>
          <span class="pogi-sm__dims">${SVG_RULER}${marketSize(['pad_medium', 'pad_large', 'pad_xl'][p.rank], this.dialog.dataset.country)}</span>
          <div class="pogi-sm__glyphtile">
            <span class="pogi-sm__glyphbox"><span class="pogi-sm__glyph" style="width:${(p.wc * scale).toFixed(0)}px;height:${(p.hc * scale).toFixed(0)}px"></span></span>
            <span class="pogi-sm__glyphcap">${esc(this.t('actual_proportions', 'actual proportions'))}</span>
          </div>
          <p class="pogi-sm__ctx">${this.t('pads_ctx_html', "You'll go through about <b>[[month]] pads a month</b>, based on roughly [[changes]] changes a day.", { month: this.numberFormat.format(month), changes: this.numberFormat.format(changes) })}</p>`;
        if (this.cta) this.cta.disabled = false;
        setTranslatedText(this.cta, this.t('pads_cta', 'Select this size · [[name]]', { name: padName }));
        setTranslatedText(this.help, this.t('pads_help', "We'll set your size in the buy box. Add Subscribe & Save there for 10% off if you like."));
      } else {
        out.classList.add('pogi-sm__result--bags');
        this.syncFormatOptions();
        this.state.perDay = this.currentDailyUsage();
        this.rec = { perDay: this.state.perDay };
        const recommendation = this.pickCount();
        const pick = recommendation?.pick;
        const parts = packParts(pick?.value);
        const estimate = recommendation ? this.buyBox?.getUsageEstimate?.(recommendation.totalBags) : null;
        const duration = estimate ? this.buyBox.usageDurationText(estimate) : '';
        const fmt = this.currentFormatName();
        const scent = this.currentScentName();
        const subscribed = Boolean(this.buyBox?.subscribe);
        const cadence = subscribed ? this.currentCadenceName() : '';
        const purchase = subscribed
          ? this.t('bags_purchase_subscribe', 'Subscribe & Save')
          : this.t('bags_purchase_onetime', 'One-time purchase');
        const cadenceTerms = subscribed && cadence
          ? this.t('bags_subscription_cadence', 'Delivery: [[cadence]]', { cadence })
          : '';
        // Keep the price attached to purchase intent on narrow screens. The
        // groups render inline on desktop, then become intentional rows on
        // mobile so a separator/price can never dangle by itself.
        const selection = [
          { kind: 'product', text: [scent, fmt].filter(Boolean).join(' · ') },
          { kind: 'quantity', text: this.t('ue_calc_quantity', 'Selected quantity: [[quantity]]', { quantity: this.numberFormat.format(this.currentQuantity()) }) },
          { kind: 'purchase', text: [purchase, pick?.price
            ? this.t('ue_calc_price_each', '[[price]] per pack', { price: pick.price }) : ''].filter(Boolean).join('\u00a0·\u00a0') },
          { kind: 'cadence', text: cadenceTerms },
        ].filter((group) => group.text);
        const eyebrow = recommendation?.shortfall
          ? this.t('bags_result_largest', 'Best fit in this format')
          : this.t('bags_result_fit', 'Pogi’s pick');
        out.innerHTML = pick ? `
          <span class="pogi-sm__result-main">
            <span class="pogi-sm__eyebrow">${esc(eyebrow)}</span>
            <span class="pogi-sm__big pogi-sm__big--pack">${esc(parts.headline)}</span>
            ${parts.detail ? `<span class="pogi-sm__unit">${esc(parts.detail)}</span>` : ''}
          </span>
          <span class="pogi-sm__result-details">
            ${selection.length ? `<span class="pogi-sm__selection">${selection.map((group, index) => `${index ? '<span class="pogi-sm__selection-separator"> · </span>' : ''}<span class="pogi-sm__selection-group pogi-sm__selection-group--${group.kind}">${esc(group.text)}</span>`).join('')}</span>` : ''}
            ${duration ? `<span class="pogi-sm__ctx">${esc(this.t('ue_calc_coverage', 'About [[duration]] at [[per_day]] bags a day.', { duration, per_day: this.numberFormat.format(this.state.perDay) }))}</span>` : ''}
            ${recommendation?.shortfall ? `<span class="pogi-sm__ctx">${esc(this.t('ue_calc_shortfall', 'This selection covers less than your target.'))}</span>` : ''}
            ${estimate?.unit === 'months' ? `<span class="pogi-sm__help">${esc(this.t('ue_bags_note', 'Estimated using 30-day months.'))}</span>` : ''}
          </span>` : '';
        if (this.cta) this.cta.disabled = !pick || this.busy || !this.inputsValid() || (subscribed && !cadence);
        setTranslatedText(this.cta, pick
          ? this.t('ue_calc_apply', 'Use this pack · [[count]]', { count: parts.headline })
          : this.t('bags_cta_unavailable', 'Recommendation unavailable'));
        setTranslatedText(this.help, this.t('ue_calc_help', 'Sets this pack in the buy box. Review your selection before adding to cart.'));
      }
    }

    /* ---- bags: read CURRENT format + its rendered counts, pick one ------ */
    currentCounts() {
      // The buy box re-renders count buttons per format, so the visible ones
      // ARE the current format's counts. (bags=[data-pogi-size], comp=[data-pogi-count])
      if (!this.buyBox) return [];
      return [...this.buyBox.querySelectorAll(this.countSel)]
        .filter((button) => button.offsetParent !== null || button.style.display !== 'none')
        .map((b) => {
          const value = b.dataset.pogiSize || b.dataset.pogiCount;
          const explicit = parseInt(b.dataset.pogiBags, 10);
          const price = b.querySelector('[data-pill-price], .pogi-bb__count-price')?.textContent?.trim() || '';
          return { value, bags: explicit > 0 ? explicit : bagsIn(value), price, el: b };
        })
        .filter((count) => (
          count.value
          && count.bags > 0
          && (typeof this.buyBox.isSizingCountAvailable !== 'function'
            || this.buyBox.isSizingCountAvailable(count.value))
        ));
    }
    currentFormatName() {
      if (!this.buyBox) return '';
      const f = this.buyBox.format; // bags: string; compostable: object {name}
      if (f && typeof f === 'object' && f.name) return f.name;
      if (typeof f === 'string' && f) return f;
      const lab = this.buyBox.querySelector('[data-pogi-format-label]');
      if (lab && lab.textContent.trim()) return lab.textContent.trim();
      const on = this.buyBox.querySelector('[data-pogi-format][aria-checked="true"]');
      return on ? on.textContent.trim() : '';
    }
    currentFormatKey() {
      return this.buyBox?.querySelector('[data-pogi-format][aria-checked="true"]')?.dataset?.pogiFormat || '';
    }
    currentCadenceName() {
      return this.buyBox?.querySelector('[data-pogi-freq][aria-checked="true"]')?.textContent?.trim() || '';
    }
    currentPlanId() {
      return this.buyBox?.closest('.shopify-section')?.querySelector('input[name="selling_plan"]')?.value || '';
    }
    formatOptions() {
      if (!this.buyBox) return [];
      return [...this.buyBox.querySelectorAll('[data-pogi-formats] [data-pogi-format]')].map((source) => ({
        key: source.dataset.pogiFormat || '',
        name: source.querySelector('.pogi-bb__format-name')?.textContent?.trim() || source.textContent.trim(),
        // Reuse the source card's own trusted SVG so the modal and buy box speak
        // the same visual language without maintaining a second glyph map.
        glyph: source.querySelector('.pogi-bb__format-glyph svg')?.outerHTML || '',
        available: typeof this.buyBox.isSizingFormatAvailable === 'function'
          ? this.buyBox.isSizingFormatAvailable(source.dataset.pogiFormat || '')
          : (!source.disabled && source.getAttribute('aria-disabled') !== 'true'),
        selected: source.getAttribute('aria-checked') === 'true',
        source,
      })).filter((option) => option.key && option.name);
    }
    renderFormatOptions() {
      const section = this.body.querySelector('[data-sm-format-section]');
      const group = this.body.querySelector('[data-sm-formats]');
      const current = this.body.querySelector('[data-sm-format-current]');
      if (!section || !group || !current) return;
      const options = this.formatOptions();
      const selected = options.find((option) => option.selected);
      section.hidden = options.length <= 1;
      setTranslatedText(current, selected?.name || this.currentFormatName());
      const currentGlyph = this.body.querySelector('[data-sm-format-current-glyph]');
      if (currentGlyph) {
        currentGlyph.innerHTML = selected?.glyph || '';
        currentGlyph.hidden = !selected?.glyph;
      }
      group.innerHTML = options.length > 1 ? options.map((option) => `
        <button type="button" class="pogi-sm__format" role="radio" data-sm-format="${esc(option.key)}"
          aria-checked="${option.selected}" aria-disabled="${!option.available}" tabindex="${option.selected ? '0' : '-1'}" ${option.available ? '' : 'disabled'}>
          <span class="pogi-sm__format-check" aria-hidden="true">✓</span>
          ${option.glyph ? `<span class="pogi-sm__format-glyph" aria-hidden="true">${option.glyph}</span>` : ''}
          <span class="pogi-sm__format-name">${esc(option.name)}</span>
          ${option.available ? '' : `<span class="pogi-sm__format-status">${esc(this.t('bags_format_unavailable', 'Unavailable with your current options'))}</span>`}
        </button>`).join('') : '';
      this.syncFormatOptions();
    }
    syncFormatOptions() {
      const choices = new Map(this.formatOptions().map((option) => [option.key, option]));
      const current = this.body.querySelector('[data-sm-format-current]');
      const selected = [...choices.values()].find((option) => option.selected);
      if (current) setTranslatedText(current, selected?.name || this.currentFormatName());
      const currentGlyph = this.body.querySelector('[data-sm-format-current-glyph]');
      if (currentGlyph) {
        currentGlyph.innerHTML = selected?.glyph || '';
        currentGlyph.hidden = !selected?.glyph;
      }
      const cards = [...this.body.querySelectorAll('[data-sm-format]')];
      cards.forEach((card) => {
        const option = choices.get(card.dataset.smFormat);
        const selected = Boolean(option?.selected);
        const available = Boolean(option?.available);
        card.setAttribute('aria-checked', String(selected));
        card.setAttribute('aria-disabled', String(!available));
        card.disabled = !available;
        card.tabIndex = selected && available ? 0 : -1;
      });
      if (!cards.some((card) => card.tabIndex === 0)) {
        const first = cards.find((card) => !card.disabled);
        if (first) first.tabIndex = 0;
      }
    }
    formatChooserOpen() {
      const chooser = this.body?.querySelector('[data-sm-format-chooser]');
      return Boolean(chooser && !chooser.hidden);
    }
    openFormatChooser() {
      const chooser = this.body.querySelector('[data-sm-format-chooser]');
      const toggle = this.body.querySelector('[data-sm-format-toggle]');
      if (!chooser || !toggle) return;
      this.renderFormatOptions();
      chooser.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      const selected = this.body.querySelector('[data-sm-format][aria-checked="true"]:not(:disabled)')
        || this.body.querySelector('[data-sm-format]:not(:disabled)');
      selected?.focus({ preventScroll: true });
    }
    closeFormatChooser({ focus = false } = {}) {
      const chooser = this.body?.querySelector('[data-sm-format-chooser]');
      const toggle = this.body?.querySelector('[data-sm-format-toggle]');
      if (!chooser || !toggle) return;
      chooser.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      if (focus && this.dialog.open) toggle.focus({ preventScroll: true });
    }
    roveFormat(event) {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const current = event.target.closest('[data-sm-format]');
      const cards = [...this.body.querySelectorAll('[data-sm-format]:not(:disabled)')];
      const index = cards.indexOf(current);
      if (index < 0 || cards.length < 2) return;
      event.preventDefault();
      const next = event.key === 'Home' ? cards[0]
        : event.key === 'End' ? cards[cards.length - 1]
          : (event.key === 'ArrowRight' || event.key === 'ArrowDown')
            ? cards[(index + 1) % cards.length]
            : cards[(index - 1 + cards.length) % cards.length];
      next.focus({ preventScroll: true });
      next.click();
    }
    async settleBuyBoxSelection(action) {
      const section = this.buyBox?.closest('.shopify-section') || this.buyBox;
      let eventSeen = false;
      let selectionPromise = null;
      const onSelect = (event) => {
        if (eventSeen) return;
        eventSeen = true;
        selectionPromise = Promise.resolve(event.promise);
      };
      section?.addEventListener('shopify:product:select', onSelect);
      section?.addEventListener('product-select', onSelect);
      try {
        action();
        await Promise.resolve();
      } finally {
        section?.removeEventListener('shopify:product:select', onSelect);
        section?.removeEventListener('product-select', onSelect);
      }
      if (eventSeen) {
        await selectionPromise;
      } else {
        const frame = () => new Promise((resolve) => {
          if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(resolve);
          else window.setTimeout(resolve, 0);
        });
        await frame();
        await frame();
      }
    }
    async selectFormat(card) {
      if (this.busy || card.disabled) return;
      const key = card.dataset.smFormat;
      const originalKey = this.currentFormatKey();
      if (!key) return;
      if (key === originalKey) {
        this.closeFormatChooser({ focus: true });
        return;
      }
      const choice = this.formatOptions().find((option) => option.key === key && option.available);
      if (!choice) return;
      const wasSubscribed = Boolean(this.buyBox?.subscribe);
      const originalCadence = this.currentCadenceName();
      const originalPlanId = this.currentPlanId();
      const originalQuantity = this.currentQuantity();
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      this.busy = true;
      this.dialog.setAttribute('aria-busy', 'true');
      card.setAttribute('aria-busy', 'true');
      if (this.cta) this.cta.disabled = true;
      let applied = false;
      try {
        await this.settleBuyBoxSelection(() => choice.source.click());
        const consentPreserved = this.currentQuantity() === originalQuantity && (!wasSubscribed || (
          Boolean(this.buyBox?.subscribe)
          && this.currentCadenceName() === originalCadence
          && this.currentPlanId() === originalPlanId
        ));
        applied = this.currentFormatKey() === key && consentPreserved;
        if (!applied) {
          const original = this.formatOptions().find((option) => option.key === originalKey && option.available);
          if (original && this.currentFormatKey() !== originalKey) {
            await this.settleBuyBoxSelection(() => original.source.click());
          }
        }
      } catch (_) {
        // The buy box owns its existing failure surface; keep the modal open.
      } finally {
        this.busy = false;
        this.dialog.removeAttribute('aria-busy');
        card.removeAttribute('aria-busy');
        this.syncFormatOptions();
        this.compute();
        if (applied) {
          const changedName = this.currentFormatName();
          setTranslatedText(
            this.body.querySelector('[data-sm-format-announcer]'),
            this.t('bags_format_changed', 'Bag style changed to [[format]].', { format: changedName }),
          );
          this.closeFormatChooser({ focus: true });
        }
        else if (card.isConnected && !card.disabled) card.focus({ preventScroll: true });
        if (window.scrollX !== scrollX || window.scrollY !== scrollY) window.scrollTo(scrollX, scrollY);
      }
    }
    currentScentName() {
      if (!this.buyBox || this.kind === 'compostable') return '';
      if (typeof this.buyBox.tile === 'string' && this.buyBox.tile.trim()) return this.buyBox.tile.trim();
      const lab = this.buyBox.querySelector('[data-pogi-tile-label]');
      if (lab && lab.textContent.trim()) return lab.textContent.trim();
      const on = this.buyBox.querySelector('[data-pogi-tile][aria-checked="true"]');
      return on?.dataset?.pogiTile?.trim() || '';
    }
    pickCount() {
      const counts = this.currentCounts();
      return this.rec ? recommendBagCount(counts, this.rec.perDay, this.state.targetMonths * 30, this.currentQuantity()) : null;
    }

    /* ---- write-back: native variant selection via the buy box's buttons - */
    apply() {
      if (!this.buyBox || !this.rec) return false;
      if (this.kind === 'pads') {
        // Locale-proof: the buy box renders the size tiles in the size axis's own
        // order (Medium, Large, XL → rank 0,1,2), one [data-pogi-tile] per size in
        // that order, on EN and /ja alike (Shopify translates the labels but keeps
        // option-value order). Click the rank-th tile. Fall back to the exact EN
        // option-value string (works on the EN store if order ever shifts).
        const tiles = [...this.buyBox.querySelectorAll('[data-pogi-tile]')];
        let btn = (this.rec.rank != null && this.rec.rank < tiles.length) ? tiles[this.rec.rank] : null;
        if (!btn) btn = this.buyBox.querySelector(`[data-pogi-tile="${cssEsc(this.rec.sizeValue)}"]`);
        if (!btn) return false;
        btn.click();
        return true;
      } else {
        const recommendation = this.pickCount();
        const pick = recommendation?.pick;
        if (!pick || typeof this.buyBox.applySizingRecommendation !== 'function') return false;
        const applied = this.buyBox.applySizingRecommendation(pick.value);
        if (applied) this.reviewValue = pick.value;
        return applied;
      }
    }
  }

  // CSS.escape fallback for attribute-selector values (parens/spaces in labels).
  const cssEsc = (s) => (window.CSS && CSS.escape) ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&');

  const init = () => document.querySelectorAll('[data-pogi-sizing-modal]:not([data-sm-ready])').forEach((d) => {
    d.setAttribute('data-sm-ready', '1');
    try { new SizingModal(d); } catch (e) { /* never block the PDP */ }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
